import axios from 'axios';

// Records one step of the pre-purchase funnel in OUR database
// (POST /api/v1/funnel_events -> FunnelEvent), so the nightly report can count
// gallery views, package views, builder starts, cart/checkout views and
// payment failures per school and per family. The event names and what each
// one means: "Funnel events" in docs/purchase_flow.md.
//
// Measurement only, and it must never get in a parent's way:
//   - fire-and-forget: navigator.sendBeacon (survives a page unload), with an
//     axios POST when the browser has no beacon or refuses to queue it;
//   - NEVER throws -- every caller is a page render or a payment error handler;
//   - gallery_viewed / package_viewed are sent once per student (and package)
//     per page session, and yearbook_viewed once per student, because the
//     views that fire them re-render freely.
//
// The body is form-encoded (URLSearchParams), not JSON: it is a CORS-safelisted
// type, which is what lets every browser queue it as a beacon, and Rails parses
// it natively. The customer is never sent -- the server takes it from the
// session cookie the beacon carries.
//
// The URL is absolute on purpose: the customer app sets
// axios.defaults.baseURL = '/api/v1', and a relative '/api/v1/...' would be
// joined onto it ('/api/v1/api/v1/...'); an absolute URL ignores baseURL, and
// the shop store's ERB islands (which set no baseURL) get the same address.
export const FUNNEL_ENDPOINT = '/api/v1/funnel_events';

export const FUNNEL_EVENTS = Object.freeze({
  GALLERY_VIEWED: 'gallery_viewed',
  PACKAGE_VIEWED: 'package_viewed',
  BUILDER_STARTED: 'builder_started',
  CART_VIEWED: 'cart_viewed',
  CHECKOUT_VIEWED: 'checkout_viewed',
  PAYMENT_FAILED: 'payment_failed',
  YEARBOOK_VIEWED: 'yearbook_viewed',
});

const DEDUPED = new Set([FUNNEL_EVENTS.GALLERY_VIEWED, FUNNEL_EVENTS.PACKAGE_VIEWED, FUNNEL_EVENTS.YEARBOOK_VIEWED]);
// A gallery or package list is per pricelist, so a second pricelist is a second
// view; a yearbook offer is one per student, whichever package carries it.
const DEDUPED_PER_PACKAGE = new Set([FUNNEL_EVENTS.GALLERY_VIEWED, FUNNEL_EVENTS.PACKAGE_VIEWED]);
// A property value is a provider, a code, a short message, a path. The server
// refuses a properties hash over 2KB, so trim long error text here instead.
const MAX_VALUE_LENGTH = 300;

// Page session = this module's lifetime: it survives client-side navigation
// within the app and resets on a full page load.
const sent = new Set();

const ID_FIELDS = {
  cartId: 'cart_id',
  studentId: 'student_id',
  studentImageId: 'student_image_id',
  schoolId: 'school_id',
  packageId: 'package_id',
  optionId: 'option_id',
  orderPackageId: 'order_package_id',
  messageId: 'message_id',
};

const present = (value) => value !== undefined && value !== null && value !== '';

const csrfToken = () => {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.content : undefined;
};

// Campaign and parent-ready links carry ?message_id= (see useLinkAttribution).
// The server also falls back to the cart's stamped message_id when this is gone.
const messageIdFromUrl = () => new URLSearchParams(window.location.search).get('message_id');

const buildBody = (name, ids, properties) => {
  const body = new URLSearchParams();
  body.append('name', name);

  Object.entries(ID_FIELDS).forEach(([key, param]) => {
    if (present(ids[key])) body.append(param, String(ids[key]));
  });
  if (!present(ids.messageId)) {
    const fromUrl = messageIdFromUrl();
    if (present(fromUrl)) body.append('message_id', fromUrl);
  }

  const props = { path: window.location.pathname, ...(properties || {}) };
  Object.entries(props).forEach(([key, value]) => {
    if (!present(value) || typeof value === 'object') return;
    body.append(`properties[${key}]`, String(value).slice(0, MAX_VALUE_LENGTH));
  });

  const token = csrfToken();
  if (token) body.append('authenticity_token', token);
  return body;
};

const dedupeKey = (name, ids) => [
  name,
  ids.studentId || ids.studentImageId || '',
  DEDUPED_PER_PACKAGE.has(name) ? (ids.packageId || '') : '',
].join(':');

// trackFunnel('gallery_viewed', { studentId, cartId, packageId, properties: { surface: 'multi_pose_gallery' } })
// Returns true when the event was handed to the browser, false when it was a
// duplicate or could not be sent. Callers never need to look.
export function trackFunnel(name, { properties, ...ids } = {}) {
  try {
    if (DEDUPED.has(name)) {
      const key = dedupeKey(name, ids);
      if (sent.has(key)) return false;
      sent.add(key);
    }

    const body = buildBody(name, ids, properties);

    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function'
      && navigator.sendBeacon(FUNNEL_ENDPOINT, body)) {
      return true;
    }

    const token = csrfToken();
    axios
      .post(`${window.location.origin}${FUNNEL_ENDPOINT}`, body, { headers: token ? { 'X-CSRF-Token': token } : {} })
      .catch(() => {});
    return true;
  } catch (e) {
    return false;
  }
}

// The code and message out of whatever a payment error handler holds: a Stripe
// error ({ code, decline_code, type, message }), an axios error (the order API
// answers 422 with { data: [messages] }), an Error, or a plain string.
// Never the card: only the provider's own code and message are read.
export const describePaymentError = (error) => {
  try {
    if (!error) return {};
    if (typeof error === 'string') return { message: error };

    const responseData = error.response && error.response.data;
    const apiMessages = responseData && responseData.data;
    const message = (Array.isArray(apiMessages) && apiMessages.join('; '))
      || (responseData && typeof responseData.error === 'string' && responseData.error)
      || error.message;

    return {
      code: error.decline_code || error.code || (error.response && error.response.status) || undefined,
      error_type: error.type,
      message,
    };
  } catch (e) {
    return {};
  }
};

// payment_failed, from inside a payment error handler. `provider` is one of
// braintree | stripe_wallet | stripe | paypal | afterpay; `stage` names the
// step that failed (e.g. confirm_card_payment). Never throws.
export function trackPaymentFailure(provider, {
  stage, error, properties, ...ids
} = {}) {
  try {
    return trackFunnel(FUNNEL_EVENTS.PAYMENT_FAILED, {
      ...ids,
      properties: {
        provider, stage, ...describePaymentError(error), ...(properties || {}),
      },
    });
  } catch (e) {
    return false;
  }
}

// Test seam: forget what this page session already sent.
export const resetFunnelDedupe = () => sent.clear();

export default trackFunnel;
